import { useEffect, useState } from 'react'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import { fetchRest } from '@/common/api/fetcher'

// 取り込み結果の行（エラー・警告）
interface ImportRow {
  row_number: number
  severity: string
  reason: string
}

interface ImportRun {
  id: number
  inflow_source_label: string
  inflow_detail: string | null
  file_name: string | null
  status: string
  status_label: string
  total_rows: number
  created_count: number
  updated_count: number
  skipped_count: number
  failed_count: number
  warning_count: number
  triggered_by_name: string | null
  finished_at: string | null
  rows: ImportRow[]
}

interface Preview {
  total_rows: number
  recognized_columns: string[]
  ignored_columns: string[]
  error_count: number
  warning_count: number
  rows: ImportRow[]
  samples: Record<string, unknown>[]
}

const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT =
  'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'

const STATUS_OPTIONS = [
  { value: 'pending', label: '未確認（1件ずつ確認してから公開する）' },
  { value: 'published', label: '公開（移行元で公開済みのものをそのまま出す）' },
  { value: 'hidden', label: '非公開' },
  { value: 'later', label: '保留' }
]

const SEVERITY_STYLE: Record<string, string> = {
  error: 'bg-surface-critical text-critical',
  warning: 'bg-surface-warning text-warning'
}

const RowList = ({ rows }: { rows: ImportRow[] }) => (
  <div className='border-secondary mt-2 max-h-56 overflow-auto rounded-lg border'>
    <table className='w-full text-size-13'>
      <thead>
        <tr className='bg-fill-surface-tertiary'>
          <th className='px-2 py-1 text-left font-normal'>行</th>
          <th className='px-2 py-1 text-left font-normal'>区分</th>
          <th className='px-2 py-1 text-left font-normal'>内容</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.row_number}-${i}`} className='border-secondary border-t'>
            <td className='px-2 py-1 tabular-nums'>{r.row_number}</td>
            <td className='px-2 py-1'>
              <span
                className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${
                  SEVERITY_STYLE[r.severity] ?? ''
                }`}
              >
                {r.severity === 'error' ? 'エラー' : '警告'}
              </span>
            </td>
            <td className='px-2 py-1'>{r.reason}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

interface Props {
  siteId: string | undefined
  onImported(): void
  onClose(): void
}

/** レビューの取り込み（モール・他社レビュー収集システムからの移行）。
 *
 * 取り込んだレビューは依頼メール経由のものと同じ一覧に並ぶが、流入経路で区別できる。
 */
export default function UGCReviewImportPanel({
  siteId,
  onImported,
  onClose
}: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [inflowDetail, setInflowDetail] = useState('')
  const [defaultStatus, setDefaultStatus] = useState('pending')
  const [overwrite, setOverwrite] = useState(false)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [result, setResult] = useState<ImportRun | null>(null)
  const [runs, setRuns] = useState<ImportRun[]>([])
  const [busy, setBusy] = useState<'preview' | 'import' | null>(null)
  const [error, setError] = useState('')

  const loadRuns = () => {
    fetchRest<{ items?: ImportRun[] }>(`/api/sites/${siteId}/ugc/review-import/runs?limit=5`)
      .then((d) => setRuns(d.items || []))
      .catch(() => {})
  }

  useEffect(loadRuns, [siteId])

  const post = async (path: string, withOptions: boolean) => {
    if (!file) return null
    const form = new FormData()
    form.append('file', file)
    if (withOptions) {
      form.append('inflow_detail', inflowDetail)
      form.append('default_status', defaultStatus)
      form.append('overwrite_existing', String(overwrite))
    }
    // Content-Type は付けない（fetchRest が FormData のときに落とす）。境界（boundary）
    // 付きの multipart はブラウザに決めさせる必要があり、application/json のまま送ると
    // サーバがボディを解釈できず 422 になる
    try {
      return await fetchRest<Preview & ImportRun>(`/api/sites/${siteId}/${path}`, {
        method: 'POST',
        body: form
      })
    } catch (res) {
      if (!(res instanceof Response)) throw res
      const body = await res.json().catch(() => ({}))
      // FastAPI の検証エラー（422）は detail が配列で返る。そのまま文字列にすると
      // 画面に [object Object] と出て、何が悪いのか分からなくなる
      const detail = body.detail
      const message = Array.isArray(detail)
        ? detail.map((d: { msg?: string }) => d?.msg ?? JSON.stringify(d)).join(' / ')
        : typeof detail === 'string'
          ? detail
          : `取り込みに失敗しました（HTTP ${res.status}）`
      throw new Error(message, { cause: res })
    }
  }

  const handlePreview = async () => {
    setError('')
    setResult(null)
    setBusy('preview')
    try {
      setPreview(await post('ugc/review-import/preview', false))
    } catch (e) {
      setPreview(null)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const handleImport = async () => {
    setError('')
    setBusy('import')
    try {
      const run = await post('ugc/review-import', true)
      setResult(run)
      setPreview(null)
      loadRuns()
      onImported()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className={`${CARD} mb-4 p-4`}>
      <div className='flex items-start justify-between'>
        <div>
          <div className='text-primary text-sm font-semibold'>レビューの取り込み</div>
          <p className='text-secondary mt-1 text-xs'>
            モールや他社のレビュー収集システムから移行したレビューを CSV
            で取り込みます。取り込んだレビューは「CSV取り込み」として記録され、
            依頼メール経由のものと一覧で区別できます。
            W2Commerce のレビューの取り込みは、データ基盤 ＞ W2レビュー連携から設定します。
          </p>
        </div>
        <button
          type='button'
          className='text-secondary hover:text-primary text-xl leading-none'
          onClick={onClose}
          aria-label='閉じる'
        >
          ×
        </button>
      </div>

      <div className='mt-3 flex flex-wrap items-center gap-2.5'>
        <input
          type='file'
          accept='.csv,text/csv'
          className='text-size-13'
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null)
            setPreview(null)
            setResult(null)
            setError('')
          }}
        />
        <a
          href={`/api/sites/${siteId}/ugc/review-import/template`}
          className='text-emphasis text-xs underline'
        >
          ひな形をダウンロード
        </a>
      </div>

      <div className='mt-3 flex flex-wrap items-center gap-2.5'>
        <label className='text-secondary text-xs'>移行元</label>
        <input
          value={inflowDetail}
          onChange={(e) => setInflowDetail(e.target.value)}
          placeholder='例: 楽天市場'
          className={`${INPUT} w-40`}
        />
        <label className='text-secondary text-xs'>公開状態の既定</label>
        <select
          value={defaultStatus}
          onChange={(e) => setDefaultStatus(e.target.value)}
          className={`${INPUT} w-72`}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className='text-secondary flex items-center gap-1 text-xs'>
          <input
            type='checkbox'
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
          />
          取り込み済みのレビューを上書きする
        </label>
      </div>

      <div className='mt-3 flex gap-2'>
        <Button
          color={ButtonColor.SECONDARY}
          disabled={!file || busy !== null}
          loading={busy === 'preview'}
          onClick={handlePreview}
        >
          先に内容を確認
        </Button>
        <Button
          color={ButtonColor.PRIMARY}
          disabled={!file || busy !== null}
          loading={busy === 'import'}
          onClick={handleImport}
        >
          取り込む
        </Button>
      </div>

      {error && <div className='text-critical mt-3 text-size-13'>{error}</div>}

      {preview && (
        <div className='border-secondary mt-4 rounded-lg border p-3'>
          <div className='text-primary text-size-13 font-semibold'>
            取り込み前の確認（まだ取り込んでいません）
          </div>
          <div className='text-secondary mt-1 text-xs'>
            {preview.total_rows}行 ／ エラー {preview.error_count}件 ／ 警告{' '}
            {preview.warning_count}件
          </div>
          <div className='text-secondary mt-1 text-xs'>
            認識した列: {preview.recognized_columns.join(', ') || 'なし'}
          </div>
          {preview.ignored_columns.length > 0 && (
            <div className='text-secondary mt-1 text-xs'>
              使われない列: {preview.ignored_columns.join(', ')}
            </div>
          )}
          {preview.rows.length > 0 && <RowList rows={preview.rows} />}
        </div>
      )}

      {result && (
        <div className='border-secondary mt-4 rounded-lg border p-3'>
          <div className='text-primary text-size-13 font-semibold'>
            取り込み結果： {result.status_label}
          </div>
          <div className='text-secondary mt-1 text-xs'>
            {result.total_rows}行 ／ 登録 {result.created_count}件 ／ 更新{' '}
            {result.updated_count}件 ／ 見送り {result.skipped_count}件 ／ エラー{' '}
            {result.failed_count}件 ／ 警告 {result.warning_count}件
          </div>
          {(result.failed_count > 0 || result.warning_count > 0) && (
            <a
              href={`/api/sites/${siteId}/ugc/review-import/runs/${result.id}/errors.csv`}
              className='text-emphasis mt-1 inline-block text-xs underline'
            >
              エラー・警告の行を CSV でダウンロード（直してそのまま入れ直せます）
            </a>
          )}
          {result.rows.length > 0 && <RowList rows={result.rows} />}
        </div>
      )}

      {runs.length > 0 && (
        <div className='mt-4'>
          <div className='text-secondary text-xs'>直近の取り込み</div>
          <div className='border-secondary mt-1 overflow-hidden rounded-lg border'>
            <table className='w-full text-size-13'>
              <thead>
                <tr className='bg-fill-surface-tertiary'>
                  <th className='px-2 py-1 text-left font-normal'>日時</th>
                  <th className='px-2 py-1 text-left font-normal'>経路</th>
                  <th className='px-2 py-1 text-left font-normal'>ファイル</th>
                  <th className='px-2 py-1 text-left font-normal'>結果</th>
                  <th className='px-2 py-1 text-left font-normal'>内訳</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className='border-secondary border-t'>
                    <td className='px-2 py-1 whitespace-nowrap'>
                      {r.finished_at ? new Date(r.finished_at).toLocaleString() : '—'}
                    </td>
                    <td className='px-2 py-1 whitespace-nowrap'>
                      {r.inflow_source_label}
                      {r.inflow_detail ? `（${r.inflow_detail}）` : ''}
                    </td>
                    <td className='px-2 py-1'>{r.file_name || '—'}</td>
                    <td className='px-2 py-1 whitespace-nowrap'>{r.status_label}</td>
                    <td className='px-2 py-1 whitespace-nowrap tabular-nums'>
                      登録{r.created_count} / 更新{r.updated_count} / 見送り
                      {r.skipped_count} / エラー{r.failed_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
